import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/classnames.js';
import { ButtonSparkles } from './ButtonSparkles.js';

interface BaseProps {
  children: ReactNode;
  busyLabel?: ReactNode | undefined;
  busy?: boolean | undefined;
  showArrow?: boolean | undefined;
  className?: string | undefined;
}
interface AsButton extends BaseProps {
  to?: undefined;
  onClick: () => void;
  disabled?: boolean | undefined;
}
interface AsLink extends BaseProps {
  to: string;
  onClick?: (() => void) | undefined;
}
type GildButtonProps = AsButton | AsLink;

/**
 * GildButton — the primary gild CTA, rendered as the signature MOLTEN GOLD
 * button (see `.btn-primary` / `.btn-cta` in globals.css): a living liquid-gold
 * face that flows at rest, catches a periodic gleam to draw the eye, blooms on
 * hover, and burnishes down on press. No magnetism. Renders a <button> by
 * default, or a router <Link> when `to` is set. Used for Gild another, Gild it,
 * Start gilding, and (via ConnectButton) wallet sign-in.
 */
export function GildButton(props: GildButtonProps) {
  const { children, busy = false, busyLabel, showArrow = true, className } = props;

  const inner = (
    <>
      {busy && <Spinner />}
      <span>{busy ? (busyLabel ?? children) : children}</span>
      {!busy && showArrow && <Arrow />}
      <ButtonSparkles />
    </>
  );

  const shared = {
    className: cn('btn-primary btn-cta', className),
    'aria-busy': busy,
  };

  if (props.to !== undefined) {
    return (
      <Link to={props.to} onClick={props.onClick} {...shared}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" disabled={busy || props.disabled} onClick={props.onClick} {...shared}>
      {inner}
    </button>
  );
}

function Arrow() {
  return (
    <svg
      className="btn-arrow"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h13" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
      className="motion-safe:animate-spin"
    >
      <path d="M12 3a9 9 0 1 0 9 9" />
    </svg>
  );
}
